<template>

	<div ref="wrapper" class="steps-form-wrapper">
		<div v-if="hasPermissions" class="logout-row">
			<AppClickableIcon
				icon-name="iconLogout"
				@click-icon="goToLogin"
			/>
		</div>
		<div v-if="hasPermissions" class="steps-form-content">
			<div class="mb-10">
				<h3 class="mb-5">{{getString('unpaid_invoices_popup_title')}}</h3>
				<p class="business-info-caption">{{getString('unpaid_invoices_caption')}}</p>
			</div>
			<div class="undpaid-invoices-list-container">
				<div class="row">
					<AppList :items="unpaidInvoices">
						<template v-slot:cta-heading="{item}">
							<div class="row centered space-between">
								<p class="list-item-title">
									<small class="opacity-60">{{item.date + ' ('+ item.elapsed + ')'}}</small>
									<br>
									{{item.title}}
									<small v-show="item?.is_metered" class="opacity-60 commissions-phrase"><br>{{getString('other_payments_methods_commissions')}}</small>
								</p>
								<a class="pay-invoice-link" :href="item?.url" target="_blank">
									<AppButton class="invoice-cta" @click="clickPay(item)" :text="item.amount" />
								</a>
							</div>
						</template>
					</AppList>
				</div>
			</div>
			<div class="row centered flex-end mt-20 buttons-row">
				<AppButton outline small :text="getString('do_it_later')" @click="dismissUnpaidInvoicesPopup" />
			</div>
		</div>
		<div v-if="!hasPermissions" class="steps-form-content pr-10 pl-10">
			<p>{{ getString('business_setup_permission_invoices') }}</p>
			<div class="row centered flex-end pt-10">
				<AppButton small outline :text="getString('change_account')" :loading="loggingOut" @click="goToLogin" />
			</div>
		</div>
	</div>

</template>

<script>
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";

import {useUserStore} from "@/console-new/stores";
import {useLanguageStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppList from "@/shared/components/AppList.vue";
import API from "@/shared/helpers/API";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {formatDateYYMMDD, formatTimeHHmm, getLocalDateFromServer} from "@/shared/helpers/dates";

export default {
	name: "UnpaidInvoicesPopup",
	setup() {
		let userStore = useUserStore();
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let currencyStore = useCurrencyStore();
		return {currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore};
	},
	components: {AppButtonIcon, AppPopup, AppList, AppLoader, AppButton, AppClickableIcon},
	data() {
		return {
			polling: false,
			loggingOut: false
		}
	},
	computed: {
		hasPermissions() {
			if(!this.userStore || !this.userStore.currBusiness || !this.userStore.currBusiness.permissions || !this.userStore.currBusiness.permissions.length) {
				return true;
			}
			let finalPermissions = this.userStore.currBusiness.permissions;
			return finalPermissions.includes('admin');
		},
		unpaidInvoices() {
			let currInvoices = this.userStore.unpaidInvoices;
			if(!currInvoices || !currInvoices?.length || !currInvoices?.map) {
				return [];
			}
			return currInvoices.map(invoice => {
				let date = getLocalDateFromServer(invoice.created);
				let datePart = formatDateYYMMDD(date);
				let timePart = formatTimeHHmm(date);
				return {
					...invoice,
					title: this.getString('invoice_number_x', [invoice.number || '']),
					date: datePart+' '+timePart,
					elapsed: this.readableElapsedTime(date),
					amount: this.currencyStore.readableCurrencyValue(invoice.amount_due),
					url: invoice.url,
					customSlot: 'cta-heading'
				}
			});
		}
	},
	methods: {
		clickPay(item) {
			if(!item.url) {
				return;
			}
			this.startPolling();
		},
		startPolling() {
			this.polling = true;
			clearTimeout(this.pollingTimeout);
			this.pollingTimeout = setTimeout(this.refreshInvoices, 5000);
		},
		refreshInvoices() {
			let currentInvoices = JSON.stringify(this.userStore.unpaidInvoices);
			this.userStore.getBusinessUnpaidInvoices(()=>{
				let fetchedInvoices = JSON.stringify(this.userStore.unpaidInvoices);
				if(currentInvoices === fetchedInvoices && this.polling) {
					this.pollingTimeout = setTimeout(this.refreshInvoices, 5000);
				} else {
					this.polling = false;
					clearTimeout(this.pollingTimeout);
				}
			});
		},
		readableElapsedTime(date) {
			if (!(date instanceof Date) || isNaN(date.getTime())) {
				return '';
			}
			const now = new Date();
			const seconds = Math.floor((now - date) / 1000);
			const intervals = [
				{ label: this.getString('time_year'), plural: this.getString('time_years'), seconds: 31536000 },
				{ label: this.getString('time_month'), plural: this.getString('time_months'), seconds: 2592000 },
				{ label: this.getString('time_day'), plural: this.getString('time_days'), seconds: 86400 },
				{ label: this.getString('time_hour'), plural: this.getString('time_hours'), seconds: 3600 },
				{ label: this.getString('time_minute'), plural: this.getString('time_minutes'), seconds: 60 }
			];
			for (const interval of intervals) {
				const count = Math.floor(seconds / interval.seconds);
				if (count >= 1) {
					return this.getString('elapsed_time_phrase', [count, interval[count !== 1 ? 'plural' : 'label']]);
				}
			}
			return this.getString('now');
		},
		goToLogin() {
			this.loggingOut = true;
			this.userStore.logout(() => {
				this.loggingOut = false;
			});
		},
		dismissUnpaidInvoicesPopup() {
			clearTimeout(this.pollingTimeout);
			this.polling = false;
			this.userStore.dismissedUnpaidInvoicesPopup = true;
		},
	},
	mounted() {
		if(!this.hasPermissions) {
			this.$router.push('/lost');
		}
	},
	beforeUnmount() {
		clearTimeout(this.pollingTimeout);
	},
	watch: {
		'$route.path'(newVal) {
			if(newVal !== '/lost' && !this.hasPermissions) {
				this.$router.push('/lost');
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.steps-form-wrapper {
	@include centered;
	position: fixed;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	padding: 5px;
	background-color: var(--color-bg-overlay);
	backdrop-filter: blur(5px);
	-webkit-backdrop-filter: blur(5px);
	z-index: 200;
	max-width: 100vw;
	max-height: 100vh;
	overflow-y: auto;

	.logout-row {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		position: absolute;
		top: 0;
		right: 0;
		width: 100%;
		padding: 20px;
	}

	.steps-form-content {
		width: 100%;
		max-width: 600px;
		border: 1px solid var(--color-primary);
		border-radius: 26px;
		padding: 20px;
		background-color: var(--color-bg);
		max-height: calc(100vh - 50px);
		overflow-y: auto;

		.business-info-caption {
			font-size: 16px;
		}
	}

	.input-container {
		position: relative;
	}
	p.caption {
		font-size: 20px;
		color: #9CA3AF;
		margin-bottom: 20px;
	}

	@media (max-width: 920px) {

		p.caption {
			font-size: 16px;
		}
	}

}
.input-button-row {
	@include centered;
	justify-content: space-between;

	&.vat-row {
		padding: 10px;
		background-color: var(--color-alpha-1);
		border-radius: 13px;
		overflow: hidden;
	}

	.input-wrapper, .select-wrapper {
		&:not(.single-input-wrapper) {
			margin-right: 20px;
		}
	}
	.add-user-input {
		margin-right: 0;
	}
	> p {
		font-size: 16px;
		max-width: calc(100% - 410px);
	}
	&.select-row > p {
		font-size: 16px;
	}
	.input-btn-container {
		min-width: 410px;
	}
	@media (max-width: 768px) {
		&:not(.select-row) {
			justify-content: flex-end;

			.row:not(.vat-loader-row) {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.input-wrapper, .select-wrapper {
				margin-right: 0;
				max-width: 100%;
				&:not(.single-input-wrapper) {
					margin-bottom: 10px;
				}
			}
			.select-wrapper{ width: 100%; }
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
	@media (max-width: 600px) {
		> p, &.select-row > p {
			font-size: 14px;
		}
		.input-btn-container {
			min-width: unset;
			.select-wrapper{ width: auto; }
		}
		> p {
			max-width: calc(100% - 310px);
		}
	}
	@media (max-width: 480px) {
		&.select-row {
			align-items: flex-start;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				margin-right: 0;
				margin-bottom: 10px;
				max-width: unset;
				min-width: 200px;
			}
			> p {
				max-width: calc(100% - 180px);
				margin-bottom: 4px;
			}
		}

		.input-btn-container {
			min-width: 100%;
		}
	}
	@media (max-width: 400px) {
		> p, &.select-row > p {
			font-size: 12px;
		}
		.vat-loader-row {
			justify-content: flex-start;

			p {
				margin-right: 10px;
			}
		}
	}
	@media (max-width: 360px) {
		&.select-row {
			justify-content: flex-end;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				max-width: 100%;
				width: 100%;
			}
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
}
.buttons-row {
	gap: 20px;
}
.undpaid-invoices-list-container {
	border: 1px solid var(--color-primary);
	border-radius: 13px;
	background-color: var(--color-bg-primary-1);
	padding: 20px;
	max-height: 400px;
	overflow-y: auto;
}
.invoice-cta {
	margin-left: auto;
}
.commissions-phrase {
	font-size: 10px;
	line-height: 12px;
}
a.pay-invoice-link:after {
	display: none;
}
</style>
